import { createContext, ReactNode, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { usePluginspace } from "../hooks/usePluginspace";
import api from "../services/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSeller } from "../hooks/useSeller";
import { useCallback } from "react";
import { useRef } from "react";
import { Loading } from "../components/Loading";

export type PluginconfigType = {
  id: number;
  plugintemplateId: number;
  plugintypeId: number;
  content: any;
};

type PageInfo = {
  title: string;
  description: string;
  imagePath: string;
};

// Constantes
const S3_BASE_URL = "https://s3.amazonaws.com/img.ninegrid.com";
const ERROR_MESSAGE =
  "O cardápio não está disponível no momento, entre em contato com o estabelecimento.";

export type ConfigType = {
  name: string;
  webappcolor: {
    backgroundbuttonhome?: string;
    backgroundcart?: string;
    backgroundfooter?: string;
    backgroundheader?: string;
    backgroundmenu?: string;
    backgroundstatus?: string;
    backgroundstatusactive?: string;
    backgroundtitlebar?: string;
    backgroundwebapp?: string;
    fontbutton?: string;
    fontbuttonhome?: string;
    fontcart?: string;
    fontcategory?: string;
    fontheader?: string;
    fontinfocart?: string;
    fontmenu?: string;
    fontstatus?: string;
    fonttitlebar?: string;
    highlightfooter?: string;
    iconsfooter?: string;
    iconsheader?: string;
    icontitlebar?: string;
    webappbutton?: string;
    flagDiscountColor?: string;
  };
  background?: string | null;
  img?: string | null;
  icon?: string | null;
  banner?: string | null;
  info?: string | null;
  content: {
    useLogoMenu: boolean;
    colorLogo: string;
    useLogoTotvs: boolean;
    qtdProductsPagination: number;
    noConfirmed: boolean;
    noShadow: boolean;
    noUseViewMode: boolean;
    showFlagDiscount: boolean;
    pdv: {
      pluginconfigId: number;
      gatewayId: number;
      gatewayPixId: number;
      gatewayDeliveryId: number;
      termsActive: boolean;
      restrictCep: string;
      showOutStock: boolean;
      showLayout: string;
      showObservation: boolean;
      agroupCategory: boolean;
      showCategoryImages: boolean;
      noProductImages: boolean;
      allowSellCnpj: boolean;
      withDrawFee: number;
      minimumValue: number;
      sellerCheckout: number;
      noSales: boolean;
      safeMode: boolean;
      timeSafeMode: number;
      typeImage: string;
      limitCharacters?: number;
      paymentTable: boolean;
      paymentCard: boolean;
      skipEntry: boolean;
      ableUserLocation: boolean;
      sellerCartExpirationTime: string;
      internalDelivery: boolean;
      ableWinter: boolean;
      closeAccountButton: boolean;
      allowEmitNFE: boolean;
      customDeliveryFreeLabel: string;
      omitGrid: boolean;
      biometric_authentication: boolean;
      code_scanner: boolean;
      sellerCode:boolean
    };
    useCategoryList: boolean;
    orderCategory: string;
    flagDiscount: string;
    /* Usados No Atacado */
    showButtonClassification: boolean;
    showButtonCatalog: boolean;
    showClassificationName: boolean;
    showCatalogName: boolean;
    showProductInventory: boolean;
    showProductMark: boolean;
    showProductSku: boolean;
    useProductList: boolean;
    removeShadow: boolean;
    typeImage: string; // Adicionado para compatibilidade com o tipo de imagem
  };
  template?: string;
};

type AppConfigContextType = {
  getAppConfig: () => Promise<void | ConfigType>;
  getPluginconfig: (getApi?: boolean) => Promise<PluginconfigType[]>;
  getSellerAppConfig: () => Promise<void | ConfigType>;
  setTimeToValidate: (value: number) => void;
  isFromAgregador: boolean;
  linkAgregador: string;
  config: ConfigType | undefined;

  getBlockLayout: () => Promise<boolean>;
  saveBlockLayout: (value: boolean) => void;
  blockLayout: boolean;
  setBlockLayout: (value: boolean) => void;
  blockLayoutInProduct: boolean;
  saveBlockLayoutInProduct: (value: boolean) => void;
  getBlockLayoutInProduct: () => Promise<boolean>;

  redeSocial: RedeSocial[];
  getSocialMedia: () => Promise<RedeSocial[]>;
};

type RedeSocial = {
  id: number;
  name: string;
  url: string;
  type: string;
};

type AppConfigContextProviderProps = {
  children: ReactNode;
};

export const AppConfigContext = createContext({} as AppConfigContextType);

export function AppConfigContextProvider(props: AppConfigContextProviderProps) {
  const { token, getToken, getThisPluginspace } = usePluginspace();
  const { getTokenSeller } = useSeller();
  const [config, setConfig] = useState<ConfigType>();
  const [pluginconfigs, setPluginconfigs] = useState<PluginconfigType[]>();
  const [alreadyAlert, setAlreadyAlert] = useState(false);
  const [timeToValidade, setTimeToValidate] = useState(0);
  const [isFromAgregador, setIsFromAgregador] = useState(false);
  const [linkAgregador, setLinkAgregador] = useState("");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const [redeSocial, setRedeSocial] = useState<RedeSocial[]>([]);

  const [blockLayout, setBlockLayout] = useState(false);
  const [blockLayoutInProduct, setBlockLayoutInProduct] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  // Memoize getPluginconfig to avoid unnecessary re-creation and repeated API calls

  const pluginconfigPromiseRef = useRef<Promise<PluginconfigType[]> | null>(
    null,
  );
  const getPluginconfig = useCallback(
    async (fetchFromApi?: boolean) => {
      if (!fetchFromApi && pluginconfigs) {
        return pluginconfigs;
      }
      if (!fetchFromApi && pluginconfigPromiseRef.current) {
        return pluginconfigPromiseRef.current;
      }
      pluginconfigPromiseRef.current = api
        .get("/api/pluginconfig", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => {
          const pluginconfigData = response.data;
          const newConfigs = pluginconfigData.data.map(
            (config: PluginconfigType) => config,
          );
          setPluginconfigs(newConfigs);
          return newConfigs;
        })
        .catch(() => {
          return [];
        });
      return pluginconfigPromiseRef.current;
    },
    [pluginconfigs, getToken],
  );

  function verifyLastTimeQrcode() {
    if (config && config.content.pdv.safeMode && timeToValidade) {
      const now = new Date().getTime();
      const lastTime = localStorage.getItem(window.btoa("last_time_qrcode"));
      if (lastTime) {
        localStorage.removeItem("reload_app_food");
        if (
          now - parseInt(lastTime) > timeToValidade * 60 * 1000 &&
          !window.location.href.includes("card") &&
          !window.location.href.includes("table") &&
          !window.location.href.includes("synkar") &&
          !window.location.href.includes("pluginbot") &&
          !window.location.href.includes("counter") &&
          window.location.pathname.split("/")[1] &&
          !alreadyAlert
        ) {
          setAlreadyAlert(true);
          localStorage.setItem("reload_app_food", "true");
          Swal.fire({
            icon: "warning",
            title: "Atenção!",
            text: "Seu tempo de sessão acabou. Favor ler o QR code novamente para continuar",
          }).then(() => window.location.reload());
        }
      }
    }
  }

  async function getSellerAppConfig() {
    if (config) {
      return config;
    }
    let newTk = token;
    let seller = true;
    if (!token) {
      const subdomain = window.atob(window.location.pathname.split("/")[1]);
      await getToken(subdomain).then((responseToken) => {
        if (responseToken) {
          newTk = responseToken;
        }
      });
    }
    return await api
      .get("/api/appconfig", {
        headers: {
          Authorization: `Bearer ${token ? token : newTk}`,
        },
      })
      .then((response) => {
        const app = seller
          ? response.data.data.find((config: any) => config.type === "seller")
          : response.data.data.find((config: any) => config.type === "qr");

        const config: ConfigType = {
          webappcolor: app.content.webappcolor,
          name: app.name,
          info: app.info,
          background:
            app.background && app.background.includes("http")
              ? app.background
              : app.background
                ? `https://s3.amazonaws.com/img.ninegrid.com/${app.background}`
                : null,
          img:
            app.img && app.img.includes("http")
              ? app.img
              : `https://s3.amazonaws.com/img.ninegrid.com/${app.img}`,
          icon:
            app.icon && app.icon.includes("http")
              ? app.icon
              : `https://s3.amazonaws.com/img.ninegrid.com/${app.icon}`,
          banner:
            app.banner && app.banner.includes("http")
              ? app.banner
              : `https://s3.amazonaws.com/img.ninegrid.com/${app.banner}`,
          content: {
            useLogoMenu: app.content.useLogoMenu
              ? app.content.useLogoMenu
              : false,
            colorLogo: app.content.colorLogo ? app.content.colorLogo : "black",
            useLogoTotvs: app.content.useLogoTotvs
              ? app.content.useLogoTotvs
              : false,
            qtdProductsPagination: app.content.qtdProductsPagination
              ? app.content.qtdProductsPagination
              : 10,
            noShadow: app.content.noShadow ? app.content.noShadow : false,
            noConfirmed: app.content.noConfirmed
              ? app.content.noConfirmed
              : false,
            noUseViewMode: app.content.noUseViewMode
              ? app.content.noUseViewMode
              : false,
            showFlagDiscount: app.content.showFlagDiscount
              ? app.content.showFlagDiscount
              : false,

            showButtonClassification:
              app.content.showButtonClassification || false,
            showButtonCatalog: app.content.showButtonCatalog || false,
            showCatalogName: app.content.showCatalogName || false,
            showClassificationName: app.content.showClassificationName || false,
            showProductInventory: app.content.showProductInventory || false,
            showProductMark: app.content.showProductMark || false,
            showProductSku: app.content.showProductSku || false,
            useProductList: app.content.useProductList || false,
            removeShadow: app.content.removeShadow || false,
            typeImage: app.content.typeImage || "square",
            pdv: {
              pluginconfigId: app.content.pdv.pluginconfig_id,
              gatewayId: app.content.pdv.gateway_config,
              gatewayPixId: app.content.pdv.gateway_pix_config
                ? app.content.pdv.gateway_pix_config
                : 0,
              gatewayDeliveryId: app.content.pdv.gateway_delivery_config
                ? app.content.pdv.gateway_delivery_config
                : 0,
              termsActive: app.content.pdv.terms_active,
              restrictCep: app.content.pdv.restrict_cep,
              showOutStock: app.content.pdv.show_out_stock
                ? app.content.pdv.show_out_stock
                : false,
              showLayout: app.content.pdv.show_layout
                ? app.content.pdv.show_layout
                : "l1",
              showObservation: app.content.pdv.show_observation
                ? app.content.pdv.show_observation
                : false,
              agroupCategory: app.content.pdv.agroup_category
                ? app.content.pdv.agroup_category
                : false,
              showCategoryImages: app.content.pdv.show_category_images
                ? app.content.pdv.show_category_images
                : false,
              noProductImages: app.content.pdv.no_product_images
                ? app.content.pdv.no_product_images
                : false,
              allowSellCnpj: app.content.pdv.allowSellCnpj
                ? app.content.pdv.allowSellCnpj
                : false,
              //Taxa de retirada da loja.
              withDrawFee: app.content.pdv.delivery_fee
                ? app.content.pdv.delivery_fee
                : 0,
              minimumValue: app.content.pdv.minimum_value
                ? app.content.pdv.minimum_value
                : 0,

              sellerCheckout: app.content.pdv.seller_checkout
                ? parseInt(app.content.pdv.seller_checkout)
                : 1,
              noSales: app.content.pdv.no_sales
                ? app.content.pdv.no_sales
                : false,
              safeMode: app.content.pdv.safe_mode
                ? app.content.pdv.safe_mode
                : false,
              timeSafeMode: app.content.pdv.time_safe_mode
                ? app.content.pdv.time_safe_mode
                : 0,

              typeImage: app.content.pdv.product_image_type
                ? app.content.pdv.product_image_type
                : "round",
              paymentTable: app.content.pdv.payment_table
                ? app.content.pdv.payment_table
                : false,

              paymentCard: app.content.pdv.payment_card
                ? app.content.pdv.payment_card
                : false,

              skipEntry: app.content.pdv.skip_entry
                ? app.content.pdv.skip_entry
                : false,
              ableUserLocation: app.content.pdv.able_user_location
                ? app.content.pdv.able_user_location
                : false,
              sellerCartExpirationTime: app.content.pdv
                .seller_cart_expiration_time
                ? app.content.pdv.seller_cart_expiration_time
                : "",
              internalDelivery: app.content.pdv.internal_delivery
                ? app.content.pdv.internal_delivery
                : false,
              ableWinter: app.content.pdv.able_waiter
                ? app.content.pdv.able_waiter
                : false,
              closeAccountButton: app.content.pdv.close_account_button
                ? app.content.pdv.close_account_button
                : false,
              allowEmitNFE: app.content.pdv.emit_nota_fiscal
                ? app.content.pdv.emit_nota_fiscal
                : false,

              customDeliveryFreeLabel: app.content.pdv
                .custom_delivery_free_label
                ? app.content.pdv.custom_delivery_free_label
                : "",

              omitGrid: app.content.pdv.omit_grid
                ? app.content.pdv.omit_grid
                : false,
              biometric_authentication: app.content.pdv.biometric_authentication
                ? app.content.pdv.biometric_authentication
                : false,
              code_scanner: app.content.pdv.code_scanner
                ? app.content.pdv.code_scanner
                : false,
               sellerCode: app.content.pdv.seller_code ?? false,
            },
            useCategoryList: app.content.useCategoryList
              ? app.content.useCategoryList
              : false,
            orderCategory: app.content.orderCategory
              ? app.content.orderCategory
              : "category",
            flagDiscount: app.content.flagDiscount
              ? app.content.flagDiscount
              : "trigonal",
          },
          template: app.template,
        };
        setConfig(config);
        return config;
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "O cardápio não está disponível no momento, entre em contato com o estabelecimento.",
        });
      });
  }

  async function updatePageInfo() {
    try {
      const pluginspace = await getThisPluginspace();

      let image = config?.banner;

      if (pluginspace) {
        document.title = pluginspace?.name;
      }

      if (image) {
        const faviconLink: HTMLLinkElement = document.createElement("link");

        // Define os atributos do link
        faviconLink.rel = "shortcut icon";
        faviconLink.type = "image/x-icon";
        faviconLink.href = image;

        // Adiciona o link ao cabeçalho do documento
        document.head.appendChild(faviconLink);
      }

      // Atualiza a imagem do cabeçalho
    } catch (error) {
      console.log(error);
    }
  }

  async function getSocialMedia() {
    let data: RedeSocial[] = [];
    await api
      .get(
        `/api/socialnetwork`,

        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        },
      )
      .then(async (response) => {
        if (response.data && response.data.success) {
          setRedeSocial(response.data.data);
          data = response.data.data;
        } else {
          setRedeSocial([]);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    return data;
  }

  // Funções auxiliares
  function formatImageUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    if (url.includes("http")) return url;
    return `${S3_BASE_URL}/${url}`;
  }

  function getConfigType(isSeller: boolean): "seller" | "qr" {
    return isSeller ? "seller" : "qr";
  }

  function buildPdvConfig(pdvData: any): ConfigType["content"]["pdv"] {
    return {
      pluginconfigId: pdvData.pluginconfig_id,
      gatewayId: pdvData.gateway_config,
      gatewayPixId: pdvData.gateway_pix_config ?? 0,
      gatewayDeliveryId: pdvData.gateway_delivery_config ?? 0,
      termsActive: pdvData.terms_active,
      restrictCep: pdvData.restrict_cep,
      showOutStock: pdvData.show_out_stock ?? false,
      showLayout: pdvData.show_layout ?? "l1",
      showObservation: pdvData.show_observation ?? false,
      agroupCategory: pdvData.agroup_category ?? false,
      showCategoryImages: pdvData.show_category_images ?? false,
      noProductImages: pdvData.no_product_images ?? false,
      allowSellCnpj: pdvData.allowSellCnpj ?? false,
      withDrawFee: pdvData.delivery_fee ? parseFloat(pdvData.delivery_fee) : 0,
      minimumValue: pdvData.minimum_value
        ? parseFloat(pdvData.minimum_value)
        : 0,
      sellerCheckout: pdvData.seller_checkout
        ? parseInt(pdvData.seller_checkout)
        : 1,
      noSales: pdvData.no_sales ?? false,
      safeMode: pdvData.safe_mode ?? false,
      timeSafeMode: pdvData.time_safe_mode ?? 0,
      typeImage: pdvData.product_image_type ?? "round",
      limitCharacters: pdvData.limit_characteres ?? 0,
      customDeliveryFreeLabel: pdvData.custom_delivery_free_label ?? "",
      paymentTable: pdvData.payment_table ?? false,
      paymentCard: pdvData.payment_card ?? false,
      skipEntry: pdvData.skip_entry ?? false,
      ableUserLocation: pdvData.able_user_location ?? false,
      sellerCartExpirationTime: pdvData.seller_cart_expiration_time ?? "02:00",
      internalDelivery: pdvData.internal_delivery ?? false,
      ableWinter: pdvData.able_waiter ?? false,
      closeAccountButton: pdvData.close_account_button ?? false,
      allowEmitNFE: pdvData.emit_nota_fiscal ?? false,
      omitGrid: pdvData.omit_grid ?? false,
      biometric_authentication: pdvData.biometric_authentication ?? false,
      code_scanner: pdvData.code_scanner ?? false,
      sellerCode: pdvData.seller_code ?? false,
    };
  }

  function buildContentConfig(appData: any): ConfigType["content"] {
    return {
      useLogoMenu: appData.useLogoMenu ?? false,
      colorLogo: appData.colorLogo ?? "black",
      useLogoTotvs: appData.useLogoTotvs ?? false,
      qtdProductsPagination: appData.qtdProductsPagination ?? 10,
      noShadow: appData.noShadow ?? false,
      noConfirmed: appData.noConfirmed ?? false,
      noUseViewMode: appData.noUseViewMode ?? false,
      showFlagDiscount: appData.showFlagDiscount ?? false,
      showButtonClassification: appData.showButtonClassification ?? false,
      showButtonCatalog: appData.showButtonCatalog ?? false,
      showCatalogName: appData.showCatalogName ?? false,
      showClassificationName: appData.showClassificationName ?? false,
      showProductInventory: appData.showProductInventory ?? false,
      showProductMark: appData.showProductMark ?? false,
      showProductSku: appData.showProductSku ?? false,
      useProductList: appData.useProductList ?? false,
      removeShadow: appData.removeShadow ?? false,
      typeImage: appData.typeImage ?? "square",
      pdv: buildPdvConfig(appData.pdv || {}),
      useCategoryList: appData.useCategoryList ?? false,
      orderCategory: appData.orderCategory ?? "category",
      flagDiscount: appData.flagDiscount ?? "trigonal",
    };
  }

  function buildConfigObject(appData: any): ConfigType {
    return {
      webappcolor: appData.content.webappcolor,
      name: appData.name,
      info: appData.info,
      background: formatImageUrl(appData.background),
      img: formatImageUrl(appData.img),
      icon: formatImageUrl(appData.icon),
      banner: formatImageUrl(appData.banner),
      content: buildContentConfig(appData.content),
      template: appData.template,
    };
  }

  async function fetchAppConfigFromApi(
    token: string,
    configType: "seller" | "qr",
  ): Promise<ConfigType | null> {
    try {
      const response = await api.get("/api/appconfig", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.data?.data || response.data.data.length === 0) {
        throw new Error("No config data received");
      }

      const appData = response.data.data.find(
        (config: any) => config.type === configType,
      );

      if (!appData) {
        throw new Error(`Config type '${configType}' not found`);
      }

      return buildConfigObject(appData);
    } catch (error) {
      console.error("Error fetching app config:", error);
      throw error;
    }
  }

  // Função principal refatorada
  async function getAppConfig() {
    try {
      if (config) {
        return config;
      }

      const isSeller =
        !!localStorage.getItem("seller-tk") ||
        window.location.pathname.includes("seller");

      let tk = token || (await getToken());

      if (!tk) {
        const subdomain = atob(window.location.pathname.split("/")[1]);
        tk = await getToken(subdomain);
      }

      if (!tk) {
        throw new Error("Unable to obtain authentication token");
      }

      const configType = getConfigType(isSeller);
      const newConfig = await fetchAppConfigFromApi(tk, configType);

      if (!newConfig) {
        throw new Error("Failed to build config object");
      }

      setConfig(newConfig);
      return newConfig;
    } catch (error) {
      console.error("Error in getAppConfig:", error);
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: ERROR_MESSAGE,
      });
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    updatePageInfo();
  }, [config]);

  function isFromAgregator() {
    try {
      const isFromAgregator = searchParams.get("agreg");

      if (isFromAgregator) {
        localStorage.setItem("aggregator_link", isFromAgregator);
        setIsFromAgregador(true);
        setLinkAgregador(isFromAgregator);
      }
    } catch (error) {
      isFromAgregator();
    }
  }

  function loadIsFromAgregator() {
    try {
      const isFromAgregator = localStorage.getItem("aggregator_link");
      if (isFromAgregator) {
        setIsFromAgregador(true);
        setLinkAgregador(isFromAgregator);
      }
    } catch (error) {}
  }

  function saveBlockLayout(value: boolean) {
    try {
      setBlockLayout(value);
    } catch (error) {
      console.log("Error saving block layout:", error);
    }
  }

  async function getBlockLayout() {
    try {
      const config = await getAppConfig();
      setBlockLayout(!config?.content.useCategoryList);
      return !config?.content.useCategoryList;
    } catch (error) {
      setBlockLayout(false);
      return false;
    }
  }

  function saveBlockLayoutInProduct(value: boolean) {
    try {
      setBlockLayoutInProduct(value);
    } catch (error) {
      console.log("Error saving block layout:", error);
    }
  }

  async function getBlockLayoutInProduct() {
    try {
      const config = await getAppConfig();
      setBlockLayoutInProduct(!config?.content.useProductList);
      return !config?.content.useProductList;
    } catch (error) {
      setBlockLayoutInProduct(false);
      return false;
    }
  }

  useEffect(() => {
    isFromAgregator();
    loadIsFromAgregator();
    getBlockLayout();
    getBlockLayoutInProduct();
    getSocialMedia();
  }, []);
  const context = {
    getAppConfig,
    getSellerAppConfig,
    getPluginconfig,
    setTimeToValidate,
    isFromAgregador,
    linkAgregador,
    config,
    getBlockLayout,
    saveBlockLayout,
    blockLayout,
    setBlockLayout,
    blockLayoutInProduct,
    saveBlockLayoutInProduct,
    getBlockLayoutInProduct,
    getSocialMedia,
    redeSocial,
  };

  if (isLoading) {
    return (
      <AppConfigContext.Provider value={context}>
        <div
          style={{
            height: "100vh",
          }}
        >
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      </AppConfigContext.Provider>
    );
  }

  return (
    <AppConfigContext.Provider value={context}>
      {props.children}
    </AppConfigContext.Provider>
  );
}
